'use client';

import PageSection from '@c/listing/shared/PageSection';
import { useCreateListing } from '../_context';
import Toggle from '@ui/Toggle';
import { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import FormCurrencyInputFancy from '@c/forms/controls/FormCurrencyInputFancy';
import FormLabel from '@c/forms/controls/FormLabel';
import { formatCurrency, getDurationInDays } from '@util/index';
import { FEE } from '@util/firestore/listing/listing.types';
import Input from '@ui/Input';
import Select from '@ui/Select';
import { useAuth } from 'context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { getCauses } from '@util/firestore/products';

export default function FeaturesSection() {
  const { form, product, variant } = useCreateListing();
  const { userDoc } = useAuth();

  // Fetch available causes
  const { data: causes = [] } = useQuery({
    queryKey: ['causes'],
    queryFn: getCauses,
  });
  const [isSmartPricing, setIsSmartPricing] = useState(!!product.floor_price);
  const [isSmartOffers, setIsSmartOffers] = useState(
    !!product.minimum_offer_price
  );
  const [isAuction, setIsAuction] = useState(!!product.is_auction);
  const [auction, setAuction] = useState({
    starting_price: product.price ?? '0',
    starting_date: product.start_time
      ? new Date(product.start_time).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    startTimeHour: product.start_time
      ? (new Date(product.start_time).getHours() % 12)
          .toString()
          .padStart(2, '0')
      : '12',
    startTimeMinute: product.start_time
      ? new Date(product.start_time).getMinutes().toString().padStart(2, '0')
      : '00',
    duration:
      product.start_time && product.end_time
        ? getDurationInDays(product.start_time, product.end_time) + ''
        : '1',
    auctionStart: product.start_time ? product.start_time : 0,
    auctionEnd: product.end_time ? product.end_time : 0,
  });

  useEffect(() => {
    const calcAuctionTimestamps = () => {
      const hasValidAuctionData =
        auction.starting_date &&
        auction.startTimeHour &&
        auction.startTimeMinute;
      if (!hasValidAuctionData) return { start: null, end: null };

      const [year, month, day] = auction.starting_date.split('-');
      const date = new Date(+year, +month - 1, +day);
      const hours = +auction.startTimeHour;
      date.setHours(hours, +auction.startTimeMinute);
      const start = date.getTime();
      const end = start + +auction.duration * 24 * 60 * 60 * 1000;
      return { start, end };
    };

    const { start, end } = calcAuctionTimestamps();
    if (start && end) {
      setAuction((prev) => ({
        ...prev,
        auctionStart: start,
        auctionEnd: end,
      }));
      form.setValue('start_time', start);
      form.setValue('end_time', end);
    }
  }, [
    auction.duration,
    auction.starting_date,
    auction.startTimeHour,
    auction.startTimeMinute,
    form,
  ]);

  return (
    <PageSection
      title="Features"
      switchEnabled={product.has_variations}
      showSwitch={product.condition === 'New'}
      headerClassName="!w-full sm:!w-[110rem]"
    >
      {!isAuction && !product.is_auction && (
        <div className="mt-8 flex w-full items-start justify-between gap-x-8 sm:w-[110rem] sm:gap-x-4">
          <div className="flex flex-col items-start gap-x-8 sm:flex-row sm:items-center sm:gap-x-4">
            <SmartPricingIcon className="hidden h-32 w-32 sm:flex" />

            <div className="flex flex-col gap-y-2">
              <div className="flex items-center gap-x-4">
                <h3 className="text-[1.7rem] font-semibold text-black">
                  Smart Pricing
                </h3>
              </div>

              <p className={'text-zinc-600'}>
                Increase the chance of a sale by automatically lowering your
                price gradually over time
              </p>
            </div>
          </div>

          <Toggle
            enabled={isSmartPricing}
            onChange={() => {
              setIsSmartPricing(!isSmartPricing);
            }}
          />
        </div>
      )}

      {isSmartPricing && (
        <div className="mt-8 flex w-full flex-col gap-y-4">
          <FormLabel
            value="Floor Price"
            required
            errorMessage={form.formState.errors.floor_price?.message}
          >
            <Controller
              control={form.control}
              name={'floor_price'}
              render={({ field: { onChange, onBlur } }) => (
                <div>
                  <FormCurrencyInputFancy
                    placeholder={'0.00'}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={product.floor_price?.toString() ?? ''}
                  />
                  {!!product.floor_price &&
                    product.category !== 'Dirt Bikes' &&
                    variant === 'create-listing' && (
                      <div className="ml-2 mt-[1rem] w-full text-[1.5rem] text-brand-lightest-black">
                        <p>
                          Estimated Earnings:{' '}
                          {formatCurrency(product.floor_price * (1 - FEE))}
                        </p>
                      </div>
                    )}
                </div>
              )}
            />
          </FormLabel>

          <span className="text-[1.5rem] text-zinc-700">
            Must be less than the original price
          </span>
        </div>
      )}

      <div className="mt-16 flex w-full items-start  justify-between gap-x-8 sm:w-[110rem] sm:gap-x-4">
        <div className="flex items-center gap-x-4">
          <SmartOffersIcon className="hidden h-32 w-32 sm:flex" />

          <div className="flex flex-col gap-y-2">
            <div className="flex items-center gap-x-4">
              <h3 className="text-[1.7rem] font-semibold text-black">
                Smart Offers
              </h3>
            </div>

            <p className={'text-zinc-600'}>
              Automatically reject offers below a certain value
            </p>
          </div>
        </div>

        <Toggle
          enabled={isSmartOffers}
          onChange={() => {
            setIsSmartOffers(!isSmartOffers);
          }}
        />
      </div>

      {isSmartOffers && (
        <div className="mt-8 flex flex-col gap-y-4">
          <Controller
            control={form.control}
            name={`minimum_offer_price`}
            render={({ field: { onChange, onBlur, value } }) => (
              <div className="max-w-[20rem]">
                <FormCurrencyInputFancy
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder={'0.00'}
                  defaultValue={value?.toString()}
                  error={!!form.formState.errors?.minimum_offer_price?.message}
                />
              </div>
            )}
          />

          <span className="text-[1.5rem] text-zinc-700">
            Must be less than the original price
          </span>
        </div>
      )}

      {!!userDoc?.roles?.auctions && (
        <div className="mt-16 flex w-full items-center justify-between gap-x-8 sm:mt-8 sm:w-[110rem] sm:gap-x-4">
          <div className="flex items-center gap-x-4">
            <SmartPricingIcon className="hidden h-32 w-32 sm:flex" />

            <div className="flex flex-col gap-y-2">
              <div className="flex items-center gap-x-4">
                <h3 className="text-[1.7rem] font-semibold text-black">
                  Auction Experience
                </h3>
              </div>

              <p className={'text-zinc-600'}>
                Making an impact on the sport we all love.
              </p>
            </div>
          </div>

          <Toggle
            enabled={isAuction}
            onChange={() => {
              setIsAuction(!isAuction);
              form.setValue('is_auction', !isAuction);
              form.setValue('shipment', null);
              form.setValue('is_flat_rate', true);
            }}
          />
        </div>
      )}

      {isAuction && (
        <div className="mt-8 flex w-full flex-col gap-y-4">
          <h3 className="mb-4 text-[1.8rem] text-black">
            Fill auction details
          </h3>

          <div className="flex items-start gap-x-12">
            <div className="flex w-fit flex-col gap-y-4">
              <label className="text-[1.5rem] font-semibold">
                Bid Starting Price
              </label>

              <div className="flex h-[5rem] items-center">
                <div className="flex h-full items-center justify-center rounded-bl-2xl rounded-tl-2xl bg-[#F9E8E8] px-8 font-semibold text-brand-secondary">
                  $
                </div>

                <input
                  type="text"
                  placeholder="0.00"
                  value={auction.starting_price}
                  onChange={(e) => {
                    const value = e.target.value;
                    const parsedValue = value ? parseFloat(value) : NaN;
                    setAuction((prev) => ({
                      ...prev,
                      starting_price: isNaN(parsedValue) ? 0 : parsedValue,
                    }));
                    form.setValue('price', parsedValue);
                  }}
                  className="h-full rounded-br-2xl rounded-tr-2xl border-none bg-[#F4F4F4] px-8 py-2 text-[1.5rem] outline-none"
                />
              </div>

              {form.formState.errors.price && (
                <span className="text-[1.5rem] text-red-500">
                  {form.formState.errors.price.message}
                </span>
              )}
            </div>

            <div className="w-fit">
              <FormLabel value="Start Date" required>
                <Input
                  type="date"
                  placeholder={'mm/dd/yyyy'}
                  value={auction.starting_date}
                  onChange={(e) => {
                    setAuction((prev) => ({
                      ...prev,
                      starting_date: e.target.value,
                    }));
                  }}
                  className="!rounded-2xl"
                />
              </FormLabel>
            </div>

            <div className="w-fit">
              <FormLabel
                value="Start Time"
                required
                errorMessage={form.formState.errors.start_time?.message}
              >
                <Input
                  type="time"
                  placeholder="12:00"
                  step={900}
                  value={`${auction.startTimeHour}:${auction.startTimeMinute}`}
                  onChange={(e) => {
                    const [hour, minute] = e.target.value.split(':');
                    setAuction((prev) => ({
                      ...prev,
                      startTimeHour: hour,
                      startTimeMinute: minute,
                    }));
                  }}
                  className="!rounded-2xl"
                />
              </FormLabel>
            </div>

            <div className="w-fit">
              <FormLabel value="Auction duration" required>
                <Select
                  value={auction.duration}
                  onChange={(e) => {
                    setAuction((prev) => ({
                      ...prev,
                      duration: e.value,
                    }));
                  }}
                  options={Array(14)
                    .fill(0)
                    .map((_, i) => ({
                      id: String(i + 1),
                      value: `${i + 1}`,
                      label: `${i + 1}`,
                    }))}
                />
              </FormLabel>
            </div>
          </div>

          {/* Cause Selection */}
          <div className="mt-6">
            <FormLabel value="Select Cause" required>
              <Controller
                control={form.control}
                name="cause"
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value || ''}
                    onChange={(e) => {
                      onChange(e.value);
                    }}
                    options={[
                      { id: '', value: '', label: 'Select a cause...' },
                      ...causes.map((cause) => ({
                        id: cause.id,
                        value: cause.id,
                        label: cause.name,
                      })),
                    ]}
                    placeholder="Select a cause..."
                  />
                )}
              />
            </FormLabel>
          </div>
        </div>
      )}
    </PageSection>
  );
}

const SmartPricingIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="64"
      height="64"
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M20.6119 41.0455L20.6144 41.047L20.5171 41.2119C19.8745 42.3008 20.2718 43.7286 21.3616 44.3578L29.1648 48.8629L28.7605 49.5631C28.3361 50.2982 27.3855 50.5541 26.6499 50.1308L26.4854 50.036L26.486 50.0349L11.7214 41.5105C10.9861 41.086 10.7303 40.1357 11.1537 39.3999L11.2484 39.2353L11.2495 39.236L24.2073 16.7925C24.4517 16.3692 24.8268 16.1125 25.31 16.0384L36.229 14.3646C36.9552 14.2532 37.6371 14.6426 37.9043 15.3281L38.5424 16.9661L35.217 17.4758C34.5062 17.5848 33.929 17.9791 33.5695 18.6019L20.6119 41.0455Z"
        fill="#C41719"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M36.7179 52.1282L29.9382 48.2139L21.8344 43.5352C21.1921 43.1644 20.9684 42.3279 21.3387 41.6846L21.3397 41.6851L34.3923 19.0774C34.6077 18.7043 34.9328 18.4817 35.3589 18.4163L46.2779 16.7426C46.9169 16.6446 47.5129 16.9866 47.7468 17.587L48.643 19.8876C48.0362 20.2615 47.3654 20.5474 46.707 20.6353C45.8237 18.7943 43.2077 18.6656 42.1706 20.462L42.1716 20.4625C41.4608 21.6669 41.8753 23.2556 43.1089 23.9678L43.1094 23.9669C44.786 24.9563 46.9554 23.7314 46.9598 21.7504C47.6849 21.639 48.3966 21.3518 49.0625 20.9643L50.4096 24.4221L51.4246 23.8892L50.03 20.3092C51.7605 18.9688 53.2169 16.9031 53.7695 14.8407C54.0491 13.7968 54.0664 12.8689 53.8643 12.1145C53.3814 10.3122 51.7637 9.67309 49.9656 10.1549C47.4192 10.8372 45.0373 13.446 44.0212 15.933L39.4334 16.6363L38.7904 14.9857C38.3635 13.8899 37.2534 13.2442 36.0893 13.4226L25.1703 15.0964C24.4025 15.2141 23.7753 15.6424 23.3867 16.3155L10.3539 38.889C9.62343 40.0561 10.0676 41.7078 11.2827 42.3525L26.175 50.9506L26.1756 50.9497C26.7617 51.2956 27.46 51.3624 28.0682 51.1995C28.6714 51.038 29.2156 50.6499 29.5616 50.0731L29.8931 49.502L36.1955 53.1406C37.3748 53.8267 38.8905 53.4335 39.6022 52.229L48 37.6836L47.3667 36.5L38.6347 51.6243C38.4196 51.9685 38.1848 52.182 37.7924 52.2872C37.4691 52.3738 37.1153 52.3432 36.8025 52.1762C36.7754 52.1576 36.7472 52.1416 36.7179 52.1282ZM48.8093 17.1762L49.5959 19.1952C51.0122 18.0062 52.2053 16.2682 52.6667 14.546C53.2267 12.456 52.5775 10.6367 50.2603 11.2575C48.2641 11.7924 46.3364 13.811 45.3673 15.7266C45.4458 15.7146 45.5226 15.7023 45.5978 15.6902C46.3596 15.5682 46.9651 15.4712 47.7352 15.9159C48.2112 16.1907 48.5948 16.6256 48.8093 17.1762ZM45.7696 21.3764C45.6674 20.9948 45.4217 20.6917 45.1073 20.5103C44.427 20.1176 43.5518 20.3501 43.1574 21.0334L43.1564 21.0328C42.765 21.713 42.9979 22.587 43.6804 22.9811L43.6799 22.9821C44.7699 23.6094 46.1001 22.6099 45.7696 21.3764ZM35.1893 17.2868L38.2798 16.813L37.728 15.3966C37.4934 14.7944 36.8982 14.4543 36.259 14.5522L25.3401 16.226C24.9138 16.2913 24.5889 16.5139 24.3734 16.887L11.3208 39.4947L11.3199 39.4942C10.9504 40.1361 11.1695 40.9701 11.8177 41.3447L26.7466 49.9638L26.746 49.9648C27.3881 50.3343 28.2226 50.1147 28.5966 49.4669L28.9056 48.9317L21.2674 44.5217C20.0673 43.829 19.6646 42.2824 20.354 41.1142L20.3531 41.1136L33.4057 18.5059C33.7943 17.8329 34.4214 17.4045 35.1893 17.2868Z"
        fill="black"
      />
      <path
        d="M31.8089 30L30 36.3372H31.5461L33.3061 30.0061L31.8089 30Z"
        fill="black"
      />
      <path
        d="M35.8163 30.012L34.0685 31.937L33.4574 30.0059L32.7852 32.267L33.2924 34.5281L35.1624 32.2731L35.8163 30.012Z"
        fill="black"
      />
      <path
        d="M35.6711 36.3489L37.4739 30.0117H35.9217L34.125 36.3428L35.6711 36.3489Z"
        fill="black"
      />
      <path
        d="M39.1005 36.3609L40.8545 36.367L40.0722 32.9937L42.5289 30.0298L41.0561 30.0237L39.7422 31.637L39.3694 30.0176H37.6278L38.3611 33.1892L33.625 38.8176H35.1222L38.6911 34.6193L39.1005 36.3609Z"
        fill="black"
      />
      <path
        d="M37.3636 36.7168H37.1008L36.6914 38.1773H37.6386L37.6875 37.9635L37.0092 37.9512L37.3636 36.7168Z"
        fill="black"
      />
      <path
        d="M38.5098 37.8349L38.3876 37.9388H38.0637L37.9903 37.8471L38.2164 37.0527L38.3325 36.961H38.6503L38.7359 37.0527L38.5098 37.8349ZM38.8337 36.7227H38.2837L37.9842 36.9427L37.7031 37.951L37.8864 38.1893H38.4181L38.7359 37.9388L39.017 36.9488L38.8337 36.7227Z"
        fill="black"
      />
      <path
        d="M39.6671 37.6393L39.6182 37.8593L39.502 37.9449L39.1965 37.9388L39.1293 37.8349L39.3432 37.071L39.4715 36.9671H39.7832L39.8382 37.071L39.7954 37.2421L40.0582 37.2482L40.1315 36.9365L39.9604 36.7227H39.441L39.1109 36.9732L38.8359 37.9265L39.0376 38.1954H39.5448L39.8504 37.951L39.9298 37.6454L39.6671 37.6393Z"
        fill="black"
      />
      <path
        d="M41.3517 36.7288H41.0706L40.4045 37.3154L40.5634 36.7288L40.2945 36.7227L39.8789 38.1954H40.16L40.3006 37.6882L40.5511 37.4682L40.6917 38.2016H40.9484L40.765 37.2604L41.3517 36.7288Z"
        fill="black"
      />
      <path
        d="M41.4835 37.5291H41.9724L42.0212 37.3335H41.5324L41.6301 36.973H42.339L42.4062 36.7285H41.459L41.0312 38.2013H41.9907L42.0579 37.9569H41.3674L41.4835 37.5291Z"
        fill="black"
      />
      <path
        d="M42.581 37.3575L42.6849 36.9786H43.0698L43.1249 37.0458L43.0759 37.2719L42.9721 37.3697L42.581 37.3575ZM43.3082 37.3575L43.4182 36.9419L43.2654 36.7402H42.5076L42.0859 38.213H42.3426L42.5138 37.6019H42.6909L42.7887 38.213H43.0515L42.9476 37.6019H43.021L43.3082 37.3575Z"
        fill="black"
      />
      <path
        d="M49 30L50 27L51 30L54 31L51 32L50 35L49 32L46 31L49 30Z"
        fill="black"
      />
    </svg>
  );
};

const SmartOffersIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="64"
      height="64"
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M42.4134 19.7964V25.5667C42.4134 25.9 42.281 26.2197 42.0454 26.4553C41.8097 26.691 41.49 26.8234 41.1567 26.8234H32.0082L25.0965 33.7351V26.8234H22.8973C22.564 26.8234 22.2444 26.691 22.0087 26.4553C21.773 26.2197 21.6406 25.9 21.6406 25.5667V14.2567C21.6406 13.9234 21.773 13.6038 22.0087 13.3681C22.2444 13.1324 22.564 13 22.8973 13H36.2244"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M41.4141 12L42.4141 9L43.4141 12L46.4141 13L43.4141 14L42.4141 17L41.4141 14L38.4141 13L41.4141 12Z"
        fill="black"
      />
      <path
        d="M34.1886 20.3754C34.0229 20.1143 33.785 19.9069 33.5037 19.7785C33.1742 19.6353 32.8311 19.5258 32.4795 19.4517H32.4355H32.3852C32.113 19.3889 31.9035 19.3323 31.7569 19.2821C31.6059 19.2303 31.4715 19.1392 31.3673 19.0182C31.2555 18.8802 31.1995 18.7054 31.2103 18.5281C31.2069 18.4178 31.2309 18.3083 31.2803 18.2096C31.3296 18.1109 31.4027 18.026 31.493 17.9626C31.7326 17.8165 32.0108 17.7464 32.291 17.7615C32.5478 17.7671 32.8014 17.8204 33.0387 17.9186C33.3108 18.0237 33.5651 18.1699 33.7928 18.3521L34.3519 17.2337C34.1581 17.0785 33.9473 16.9457 33.7236 16.8379C33.4991 16.7283 33.263 16.644 33.0199 16.5865C32.9592 16.5835 32.8984 16.5835 32.8377 16.5865V15.7383H31.5119V16.6179C31.3438 16.6542 31.1796 16.7068 31.0218 16.775C30.6771 16.9307 30.3894 17.1899 30.1986 17.5164C30.0018 17.8715 29.9041 18.2731 29.9159 18.6789C29.8884 19.0975 29.9963 19.5139 30.2238 19.8665C30.3987 20.1295 30.6513 20.3316 30.9464 20.4445C31.3063 20.5714 31.6762 20.668 32.0522 20.7335H32.1528C32.3427 20.7639 32.5298 20.8102 32.712 20.8718C32.8492 20.9172 32.971 20.9999 33.0639 21.1105C33.1613 21.2382 33.2103 21.3963 33.2021 21.5567C33.2083 21.6783 33.184 21.7994 33.1313 21.9092C33.0786 22.019 32.9992 22.1138 32.9005 22.185C32.6405 22.3447 32.338 22.4214 32.0334 22.4049C31.6962 22.4047 31.3619 22.343 31.0469 22.2227C30.7349 22.1044 30.4474 21.9298 30.1986 21.7075L29.5703 22.8008C29.7263 22.9801 29.9192 23.1237 30.1358 23.2217C30.4241 23.3646 30.7276 23.4743 31.0406 23.5485C31.1919 23.5843 31.345 23.6116 31.4993 23.6302V24.4533H32.8251V23.5862C33.0104 23.5477 33.1914 23.4908 33.3654 23.4165C33.7174 23.2681 34.014 23.0133 34.2137 22.6877C34.4169 22.3392 34.5192 21.9411 34.509 21.5378C34.5289 21.1419 34.4329 20.7489 34.2326 20.4068"
        fill="black"
      />
      <path
        d="M42.6913 35.168H33.8946L26.7756 37.1975C26.4072 37.2951 26.069 37.4829 25.7914 37.744C25.5139 38.0051 25.3058 38.3312 25.1859 38.6929C25.1066 38.9555 25.0802 39.2313 25.1083 39.5042C25.1363 39.777 25.2182 40.0416 25.3493 40.2826L25.0979 40.1947H12.3238C11.8124 40.1843 11.3127 40.3487 10.9072 40.6605C10.5017 40.9723 10.2146 41.4131 10.0932 41.91C9.97826 42.4385 10.0621 42.9909 10.3287 43.4615C10.5953 43.9321 11.026 44.288 11.5384 44.461L28.604 50.1161C28.7941 50.1797 28.9966 50.1969 29.1947 50.1664L45.2047 47.7347V37.6813L42.6913 35.168Z"
        fill="#C41719"
      />
      <path
        d="M45.2033 47.7336L29.1681 50.1966C28.9701 50.2272 28.7676 50.21 28.5775 50.1464L11.5119 44.4914C11.0147 44.3301 10.5918 43.996 10.3197 43.5497C10.0476 43.1035 9.94432 42.5745 10.0287 42.0587C10.113 41.5428 10.3793 41.0743 10.7793 40.7379C11.1794 40.4015 11.6867 40.2195 12.2093 40.2249H25.0965M45.2033 37.7116L42.6899 35.1982H33.8932L26.6548 37.2655C26.1556 37.4072 25.7245 37.7247 25.4412 38.1595C25.1579 38.5943 25.0415 39.1169 25.1135 39.6308C25.1855 40.1448 25.441 40.6152 25.8329 40.9554C26.2248 41.2956 26.7265 41.4825 27.2454 41.4816H33.8932M49.6016 42.7383V33.9416M46.46 33.9102H52.7433C53.0766 33.9102 53.3962 34.0425 53.6319 34.2782C53.8676 34.5139 54 34.8335 54 35.1668V48.9902C54 49.3235 53.8676 49.6431 53.6319 49.8788C53.3962 50.1145 53.0766 50.2469 52.7433 50.2469H46.46C46.1267 50.2469 45.807 50.1145 45.5714 49.8788C45.3357 49.6431 45.2033 49.3235 45.2033 48.9902V35.1668C45.2033 34.8335 45.3357 34.5139 45.5714 34.2782C45.807 34.0425 46.1267 33.9102 46.46 33.9102Z"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
